import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Edit, Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { salesLogService } from '../../services/sales-log.service';
import { productService } from '../../services/product.service';
import type {
  LogType,
  Product,
  SalesLog,
  SalesLogInput,
  SalesLogProduct,
  SalesLogProductInput,
} from '../../types/crm';
import Loading from '../../components/Loading';

const LOG_TYPES: LogType[] = ['visit', 'call', 'sample_drop', 'follow_up', 'demo', 'other'];

const editSalesLogSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  log_type: z.enum(['visit', 'call', 'sample_drop', 'follow_up', 'demo', 'other']),
  log_date: z.string().min(1, 'Log date is required'),
  duration_minutes: z.string().optional(),
  outcome: z.string().optional(),
  notes: z.string().optional(),
});

type EditSalesLogFormData = z.infer<typeof editSalesLogSchema>;

const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const logTypeBadge = (logType: LogType): string => {
  switch (logType) {
    case 'visit':
      return 'bg-blue-100 text-blue-800';
    case 'call':
      return 'bg-indigo-100 text-indigo-800';
    case 'sample_drop':
      return 'bg-purple-100 text-purple-800';
    case 'follow_up':
      return 'bg-yellow-100 text-yellow-800';
    case 'demo':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const SalesLogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const salesLogId = id ? parseInt(id, 10) : NaN;

  const [salesLog, setSalesLog] = useState<SalesLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productForm, setProductForm] = useState({ product_id: '', quantity: '1', notes: '' });
  const [isAddingProduct, setIsAddingProduct] = useState(false);

  // Client modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientForm, setClientForm] = useState({ client_name: '', sort_order: '0' });
  const [isAddingClient, setIsAddingClient] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditSalesLogFormData>({
    resolver: zodResolver(editSalesLogSchema),
  });

  const loadSalesLog = useCallback(async () => {
    setIsLoading(true);
    try {
      const log = await salesLogService.getById(salesLogId);
      setSalesLog(log);
      reset({
        subject: log.subject,
        log_type: log.log_type,
        log_date: log.log_date,
        duration_minutes: log.duration_minutes != null ? String(log.duration_minutes) : '',
        outcome: log.outcome ?? '',
        notes: log.notes ?? '',
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [salesLogId, reset]);

  useEffect(() => {
    if (Number.isNaN(salesLogId)) return;
    loadSalesLog();
  }, [salesLogId, loadSalesLog]);

  const onSave = async (data: EditSalesLogFormData) => {
    setIsSaving(true);
    try {
      const input: Partial<SalesLogInput> = {
        subject: data.subject,
        log_type: data.log_type,
        log_date: data.log_date,
        duration_minutes: data.duration_minutes ? parseInt(data.duration_minutes, 10) : null,
        outcome: data.outcome?.trim() ? data.outcome.trim() : null,
        notes: data.notes?.trim() ? data.notes.trim() : null,
      };
      const updated = await salesLogService.update(salesLogId, input);
      setSalesLog(updated);
      setIsEditing(false);
      toast.success('Sales log updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirm('Delete this sales log? This will also delete its clients and product line items.')) return;
    try {
      await salesLogService.delete(salesLogId);
      toast.success('Sales log deleted');
      navigate('/crm/sales-logs');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ----- Product line items -----

  const openProductModal = async () => {
    setProductForm({ product_id: '', quantity: '1', notes: '' });
    setShowProductModal(true);
    try {
      const result = await productService.getAll({ per_page: 200, 'q[active_eq]': true });
      setProducts(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const onAddProduct = async () => {
    if (!productForm.product_id) {
      toast.error('Pick a product');
      return;
    }
    setIsAddingProduct(true);
    try {
      const input: SalesLogProductInput = {
        product_id: parseInt(productForm.product_id, 10),
        quantity: productForm.quantity || '1',
        notes: productForm.notes.trim() ? productForm.notes.trim() : null,
      };
      await salesLogService.addProduct(salesLogId, input);
      toast.success('Product added');
      setShowProductModal(false);
      loadSalesLog();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsAddingProduct(false);
    }
  };

  const onUpdateLineItem = async (
    line: SalesLogProduct,
    patch: Partial<SalesLogProductInput>,
  ) => {
    try {
      await salesLogService.updateProduct(salesLogId, line.id, patch);
      loadSalesLog();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const onRemoveLineItem = async (line: SalesLogProduct) => {
    if (!confirm(`Remove ${line.product?.name ?? 'this product'} from this sales log?`)) return;
    try {
      await salesLogService.removeProduct(salesLogId, line.id);
      toast.success('Product removed');
      loadSalesLog();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ----- Named clients -----

  const openClientModal = () => {
    const nextSort = salesLog ? salesLog.clients.length : 0;
    setClientForm({ client_name: '', sort_order: String(nextSort) });
    setShowClientModal(true);
  };

  const onAddClient = async () => {
    if (!clientForm.client_name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setIsAddingClient(true);
    try {
      await salesLogService.addClient(salesLogId, {
        client_name: clientForm.client_name.trim(),
        sort_order: parseInt(clientForm.sort_order || '0', 10),
      });
      toast.success('Client added');
      setShowClientModal(false);
      loadSalesLog();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsAddingClient(false);
    }
  };

  const onRemoveClient = async (clientId: number) => {
    if (!confirm('Remove this client?')) return;
    try {
      await salesLogService.removeClient(salesLogId, clientId);
      toast.success('Client removed');
      loadSalesLog();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) return <Loading />;
  if (!salesLog) return <div className="text-gray-500">Sales log not found.</div>;

  return (
    <div className="space-y-6">
      <Link to="/crm/sales-logs" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to sales logs
      </Link>

      <div className="card p-6">
        {isEditing ? (
          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            <div>
              <label htmlFor="subject" className="label">Subject</label>
              <input {...register('subject')} id="subject" type="text" className="input" />
              {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="log_type" className="label">Type</label>
                <select {...register('log_type')} id="log_type" className="input">
                  {LOG_TYPES.map((s) => (
                    <option key={s} value={s}>{titleize(s)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="log_date" className="label">Date</label>
                <input {...register('log_date')} id="log_date" type="date" className="input" />
                {errors.log_date && <p className="mt-1 text-sm text-red-600">{errors.log_date.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="duration_minutes" className="label">Duration (min)</label>
                <input
                  {...register('duration_minutes')}
                  id="duration_minutes"
                  type="number"
                  inputMode="numeric"
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="outcome" className="label">Outcome</label>
                <input {...register('outcome')} id="outcome" type="text" className="input" />
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="label">Notes</label>
              <textarea {...register('notes')} id="notes" rows={3} className="input" />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => { setIsEditing(false); loadSalesLog(); }} className="btn btn-secondary">
                <X className="h-4 w-4 inline mr-1" />
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className="btn btn-primary">
                <Save className="h-4 w-4 inline mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{salesLog.subject}</h1>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{salesLog.log_date}</span>
                  <span className="text-gray-300">·</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${logTypeBadge(salesLog.log_type)}`}>
                    {titleize(salesLog.log_type)}
                  </span>
                  {salesLog.operator && (
                    <>
                      <span className="text-gray-300">·</span>
                      <Link to={`/crm/operators/${salesLog.operator.id}`} className="text-blue-600 hover:text-blue-800">
                        {salesLog.operator.name}
                      </Link>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setIsEditing(true)} className="btn btn-secondary">
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button onClick={onDelete} className="btn btn-secondary text-red-600">
                  <Trash2 className="h-4 w-4 inline mr-1" />
                  Delete
                </button>
              </div>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Rep</dt>
                <dd className="text-gray-900">{salesLog.user.full_name}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Duration</dt>
                <dd className="text-gray-900">
                  {salesLog.duration_minutes != null ? `${salesLog.duration_minutes} min` : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Outcome</dt>
                <dd className="text-gray-900">{salesLog.outcome || '—'}</dd>
              </div>
              {salesLog.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notes</dt>
                  <dd className="text-gray-900 whitespace-pre-wrap">{salesLog.notes}</dd>
                </div>
              )}
            </dl>
          </>
        )}
      </div>

      {/* Clients section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Clients</h3>
          <button onClick={openClientModal} className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4 inline mr-1" />
            Add client
          </button>
        </div>

        {salesLog.clients.length === 0 ? (
          <div className="text-center text-gray-500 py-6">No clients added yet.</div>
        ) : (
          <ol className="space-y-2">
            {salesLog.clients.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between border border-gray-200 rounded px-3 py-2"
              >
                <span className="text-sm text-gray-900">
                  <span className="text-gray-400 mr-2">{c.sort_order}</span>
                  {c.client_name}
                </span>
                <button
                  onClick={() => onRemoveClient(c.id)}
                  className="text-red-600 hover:text-red-900"
                  aria-label={`Remove ${c.client_name}`}
                >
                  <Trash2 className="h-4 w-4 inline" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Products section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Products</h3>
          <button onClick={openProductModal} className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4 inline mr-1" />
            Add product
          </button>
        </div>

        {salesLog.products.length === 0 ? (
          <div className="text-center text-gray-500 py-6">No products added yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-2 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-4 py-2 bg-gray-50"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salesLog.products.map((line) => (
                  <tr key={line.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {line.product?.name ?? '—'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        defaultValue={line.quantity || ''}
                        onBlur={(e) => {
                          if (e.target.value !== (line.quantity || '')) {
                            onUpdateLineItem(line, { quantity: e.target.value });
                          }
                        }}
                        className="input text-right w-24"
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                      <input
                        type="text"
                        defaultValue={line.notes || ''}
                        onBlur={(e) => {
                          if (e.target.value !== (line.notes || '')) {
                            onUpdateLineItem(line, { notes: e.target.value || null });
                          }
                        }}
                        className="input"
                        placeholder="—"
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <button
                        onClick={() => onRemoveLineItem(line)}
                        className="text-red-600 hover:text-red-900"
                        aria-label={`Remove ${line.product?.name ?? 'product'}`}
                      >
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add product modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowProductModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add product line item</h3>
                  <button type="button" onClick={() => setShowProductModal(false)} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="line_product" className="label">Product</label>
                    <select
                      id="line_product"
                      className="input"
                      value={productForm.product_id}
                      onChange={(e) => setProductForm({ ...productForm, product_id: e.target.value })}
                    >
                      <option value="">Select a product…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="line_quantity" className="label">Quantity</label>
                    <input
                      id="line_quantity"
                      type="text"
                      inputMode="decimal"
                      className="input"
                      value={productForm.quantity}
                      onChange={(e) => setProductForm({ ...productForm, quantity: e.target.value })}
                    />
                  </div>

                  <div>
                    <label htmlFor="line_notes" className="label">Notes</label>
                    <input
                      id="line_notes"
                      type="text"
                      className="input"
                      value={productForm.notes}
                      onChange={(e) => setProductForm({ ...productForm, notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button type="button" onClick={onAddProduct} disabled={isAddingProduct} className="btn btn-primary">
                  {isAddingProduct ? 'Adding...' : 'Add Product'}
                </button>
                <button type="button" onClick={() => setShowProductModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add client modal */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowClientModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add client</h3>
                  <button type="button" onClick={() => setShowClientModal(false)} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="client_name" className="label">Client name</label>
                    <input
                      id="client_name"
                      type="text"
                      className="input"
                      value={clientForm.client_name}
                      onChange={(e) => setClientForm({ ...clientForm, client_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="sort_order" className="label">Sort order</label>
                    <input
                      id="sort_order"
                      type="number"
                      className="input"
                      value={clientForm.sort_order}
                      onChange={(e) => setClientForm({ ...clientForm, sort_order: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button type="button" onClick={onAddClient} disabled={isAddingClient} className="btn btn-primary">
                  {isAddingClient ? 'Adding...' : 'Add Client'}
                </button>
                <button type="button" onClick={() => setShowClientModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesLogDetail;
